import { toastController, type AlertButton, alertController } from '@ionic/vue';
import { addBreadcrumb } from '@sentry/capacitor';

export enum ToastType {
  SUCCESS = 'success',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error'
}

const toastTypeData = {
  [ToastType.SUCCESS]: {
    icon: ioniconsCheckmarkCircle,
    color: 'success'
  },
  [ToastType.INFO]: {
    icon: ioniconsInformationCircle,
    color: 'info'
  },
  [ToastType.WARNING]: {
    icon: ioniconsWarning,
    color: 'warning'
  },
  [ToastType.ERROR]: {
    icon: ioniconsCloseCircle,
    color: 'danger'
  }
};

let toast : HTMLIonToastElement | null = null;

export function useUserMessages() {
  const { t } = useEkI18n();

  /**
   * A generic alert that returns a promise that only resolves when the user has taken action, simplifying the flow of creating alerts and handlers
   * @param buttons A record of keys and display labels
   * @param cancelKey The key to return when canceling the alert. The alert won't be cancelable if left empty
   * @returns A string with the key of the selected label
   * 
   * @example
   * const answer = await openGenericAlert(
   *  'Confirm deletion',
   *  'Are you sure you wanna delete the user?', 
   *  { 
   *    cancel: t('buttons.cancel'),
   *    confirm: t('buttons.confirm'),
   *    unsure: t('buttons.unsure'),
   *  },
   *  'cancel'
   * );
   * 
   * if(answer === 'confirm'){
   *   deleteUser()
   * }
   */
  async function openAlert<T extends { [buttonKey: string]: string }>(title: string, message: string, buttons: T, cancelKey?: keyof T) {
    return new Promise<keyof T>((resolve) => {

      const alertButtons: AlertButton[] = [];

      for (const [buttonKey, label] of Object.entries(buttons)) {
        alertButtons.push({
          text: label,
          role: buttonKey === cancelKey ? 'cancel' : undefined,
          handler: () => {
            return resolve(buttonKey);
          }
        });
      }
      
      createAlert();

      async function createAlert() {
        const confirm = await alertController.create({
          header: title,
          message: message,
          backdropDismiss: cancelKey !== undefined,
          buttons: alertButtons
        });

        await confirm.present();
      }
    }); 
  }

  /**
   * Used in situations where you want to prompt the user with a yes/no or confirm/cancel alert
   * @param confirmButton Placed on the right. Defaults to `t('buttons.confirm')`  
   * @param cancelButton Placed on the left. Defaults to `t('buttons.cancel')`  
   * @returns `true` when `confirmButton` is clicked and `false` when `cancelButton` or alert backdrop is clicked
   */
  async function openConfirmAlert(title: string, message: string, confirmButton: string | null = null, cancelButton: string | null = null) {
    const answer = await openAlert(title, message, { cancel: cancelButton ?? t('buttons.cancel'), confirm: confirmButton ?? t('buttons.confirm') }, 'cancel');

    return answer === 'confirm';
  }

  /**
   * Used in situations where you want to prompt the user with a message the have have to manually dismiss
   * @param okButton Defaults to `t('buttons.ok')`  
   */
  async function openOkAlert(title: string, message: string, okButton: string | null = null) {
    await openAlert(title, message, { ok: okButton ?? t('buttons.ok') }, 'ok');
  }

  async function openToast(message:string, type: ToastType = ToastType.INFO, title: string = '', duration?: number, positionAnchor: string | undefined = undefined) {
    if (!toastTypeData[type]) {
      addBreadcrumb({
        category: 'USER_MESSAGES',
        message: 'Invalid toast type changed to info',
        level: 'info'
      });
      type = ToastType.INFO;
    }

    const resolvedDuration = duration ?? (type === ToastType.SUCCESS ? 1000 : 3000);

    if (toast) {
      toast.dismiss();
    }

    toast = await toastController.create({
      header: title,
      message,
      color: toastTypeData[type].color,
      duration: resolvedDuration,
      swipeGesture: 'vertical',
      position: 'top',
      positionAnchor,
      icon: toastTypeData[type].icon,
      buttons: [
        {
          text: t('buttons.close'),
          role: 'cancel'
        }
      ]
    });

    toast.present();
  }

  function closeToast() {
    if (toast) {
      toast.dismiss();
    }
  }

  return {
    openToast,
    closeToast,
    openAlert,
    openConfirmAlert,
    openOkAlert
  };
}
